/**
 * Calibration: when the model says it is sure, how often is it right?
 *
 * The run's `identification.calibration` groups the claims Product and Brand
 * already count by the confidence the model stated for each (its
 * `field_confidence`). A run recorded before the prompt asked has none, and
 * every helper here reads that as null rather than as a zero.
 */
const BAND_LABELS = { sure: 'sure (80%+)', unsure: 'unsure', guess: 'guessing (<40%)' };

const finite = (value) => (typeof value === 'number' && Number.isFinite(value) ? value : null);
const count = (value) => (Number.isInteger(value) && value > 0 ? value : 0);
const percent = (value) => `${Math.round(value * 100)}%`;

const readStats = (raw) => ({
  claims: count(raw.claims),
  correct: count(raw.correct),
  accuracy: finite(raw.accuracy),
  meanConfidence: finite(raw.mean_confidence),
});

const readCalibration = (raw) => {
  if (!raw || typeof raw !== 'object' || !count(raw.claims)) return null;
  const bands = (Array.isArray(raw.bands) ? raw.bands : [])
    .filter((band) => band && Object.prototype.hasOwnProperty.call(BAND_LABELS, band.band))
    .map((band) => ({ band: band.band, ...readStats(band) }));
  return { ...readStats(raw), bands };
};

export const getRunCalibration = (run = {}) => {
  const stats = run && typeof run.identification === 'object' && run.identification ? run.identification : {};
  const raw = stats.calibration && typeof stats.calibration === 'object' ? stats.calibration : {};
  return { product: readCalibration(raw.product), brand: readCalibration(raw.brand) };
};

/** The "sure" band alone, for the line under a run's Product / Brand figure. */
export const getSureBand = (calibration) => calibration?.bands.find((band) => band.band === 'sure') || null;

export const formatSureLine = (calibration) => {
  const sure = getSureBand(calibration);
  return sure && sure.accuracy !== null ? `sure → ${percent(sure.accuracy)} right (${sure.claims})` : '';
};

/** One sentence per band, plus claimed vs actual, for the figure's tooltip. */
export const describeCalibration = (calibration) => {
  if (!calibration) return '';
  const bands = calibration.bands
    .map((band) => `${BAND_LABELS[band.band]} ${band.correct}/${band.claims} right`)
    .join(' · ');
  const overall = calibration.meanConfidence !== null && calibration.accuracy !== null
    ? `claimed ${percent(calibration.meanConfidence)} sure on average, right ${percent(calibration.accuracy)}`
    : '';
  return [`When the model said it was ${bands}`, overall].filter(Boolean).join(' — ');
};

/** What an answer item claimed about itself: product (`item`) and brand confidence, 0-1 or null. */
export const getItemClaimedConfidence = (item = {}) => {
  const raw = item && typeof item.field_confidence === 'object' && item.field_confidence ? item.field_confidence : {};
  return { product: finite(raw.item), brand: finite(raw.brand) };
};

export const formatClaimedConfidence = (claimed) => {
  if (!claimed) return '';
  const parts = [];
  if (claimed.product !== null) parts.push(`product ${percent(claimed.product)}`);
  if (claimed.brand !== null) parts.push(`brand ${percent(claimed.brand)}`);
  return parts.join(' · ');
};
